/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Directive,
  Input,
  Output,
  OnInit,
  EventEmitter,
  ElementRef,
  Renderer2,
} from '@angular/core';

type SortDirection = 'asc' | 'desc' | '';
type SortType = 'string' | 'number' | 'date' | '';
const rotate: Record<string, SortDirection> = {
  asc: 'desc',
  desc: 'asc',
  '': 'asc',
};

@Directive({
  selector: '[sortableCol]',
  host: {
    '(click)': 'action()',
  },
  standalone: false,
})
export class SortColumnDirective implements OnInit {
  @Input() sortableCol = '';
  @Input() direction: SortDirection = '';
  @Input() sorttype: SortType = 'string';
  @Input() initialSort = false;

  @Output() sort = new EventEmitter<SortParamsType>();

  sortGlyph: HTMLElement;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    // add the sortGlyphs to the DOM
    this.setupDom();

    // if initialSort is true sort the table on init
    if (this.initialSort === true) {
      // switch direction since action() will switch it again
      this.direction = rotate[this.direction];

      // call action to do the initial sort of the table
      this.action();
    }
  }

  setupDom() {
    // create and append the i tag that contains the sorting glyph to the the th tag
    this.sortGlyph = this.renderer.createElement('i');
    this.renderer.setAttribute(this.sortGlyph, 'aria-hidden', 'true');
    this.renderer.addClass(this.sortGlyph, 'sort-glyph');
    this.renderer.addClass(
      this.sortGlyph,
      this.direction == 'desc' ? 'xgl-sort_up' : 'xgl-sort_down',
    );
    this.renderer.appendChild(this.elementRef.nativeElement, this.sortGlyph);
  }

  action() {
    // switch the direction of the sort
    this.direction = rotate[this.direction];

    // update styles
    this.updateStyle();

    // emit sort with updated parameters so trigger tableSort pipe
    this.sort.emit({
      direction: this.direction,
      column: this.sortableCol,
      type: this.sorttype,
    });
  }

  updateStyle() {
    // remove 'sort-active' class from all sibling th tags
    [...this.elementRef.nativeElement.parentElement.children].forEach((sib) =>
      sib.classList.remove('sort-active'),
    );
    // // add 'sort-active' class to active th tag
    this.elementRef.nativeElement.classList.add('sort-active');
    // // set correct glyph based on sorting direction
    if (this.direction === 'asc') {
      this.renderer.removeClass(this.sortGlyph, 'xgl-sort_up');
      this.renderer.addClass(this.sortGlyph, 'xgl-sort_down');
    } else if (this.direction === 'desc') {
      this.renderer.removeClass(this.sortGlyph, 'xgl-sort_down');
      this.renderer.addClass(this.sortGlyph, 'xgl-sort_up');
    } else {
      this.renderer.removeClass(this.sortGlyph, 'xgl-sort_up');
      this.renderer.addClass(this.sortGlyph, 'xgl-sort_down');
    }
  }
}
export interface SortParamsType {
  direction: string;
  column: string;
  type: string;
}
